/**
 * OAuth signup/login intent — persisted across the OAuth redirect.
 *
 * Stored in BOTH sessionStorage (primary) and localStorage (fallback) because
 * some browsers (Safari iOS, certain in-app webviews, Apple OAuth redirects)
 * can drop sessionStorage across the provider round-trip.
 *
 * A 10-minute TTL is enforced on the localStorage copy so a stale intent from
 * a previous attempt can never hijack a normal sign-in.
 *
 * Presence of this key tells useAuthState.maybeSwapSocialSession() to skip
 * the CNIC-swap orphan cleanup AND tells UserContext.loadUserProfile to defer
 * persona hydration until finalizeOAuthSession has applied the chosen persona.
 */

const KEY = 'behtari_oauth_intent';
const TTL_MS = 10 * 60 * 1000;

export type OAuthIntent = {
  intent: 'signup' | 'login';
  persona?: string; // frontend persona slug, only for signup-from-persona-page
  source?: string; // optional breadcrumb for debugging
  ts?: number;
};

export function setOAuthIntent(intent: OAuthIntent): void {
  const payload = JSON.stringify({ ...intent, ts: Date.now() });
  try { sessionStorage.setItem(KEY, payload); } catch { /* no-op */ }
  try { localStorage.setItem(KEY, payload); } catch { /* no-op */ }
}

export function getOAuthIntent(): OAuthIntent | null {
  const read = (store: Storage | undefined): OAuthIntent | null => {
    try {
      const raw = store?.getItem(KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as OAuthIntent;
      if (parsed.ts && Date.now() - parsed.ts > TTL_MS) return null;
      return parsed;
    } catch {
      return null;
    }
  };
  try {
    return read(typeof sessionStorage !== 'undefined' ? sessionStorage : undefined)
      ?? read(typeof localStorage !== 'undefined' ? localStorage : undefined);
  } catch {
    return null;
  }
}

export function clearOAuthIntent(): void {
  try { sessionStorage.removeItem(KEY); } catch { /* no-op */ }
  try { localStorage.removeItem(KEY); } catch { /* no-op */ }
}

export function hasOAuthIntent(): boolean {
  return getOAuthIntent() !== null;
}

/**
 * True when an OAuth SIGNUP with a chosen persona is mid-flight.
 * Used by UserContext + DashboardPage to defer persona hydration until
 * finalizeOAuthSession has applied the chosen persona via apply_oauth_persona.
 */
export function isOAuthSignupPending(): boolean {
  const i = getOAuthIntent();
  return !!(i && i.intent === 'signup' && i.persona);
}
